import { useFlag } from '@unleash/proxy-client-react';
import { Collapse, Divider, Radio, Slider, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { QuestionMarkSvg } from '../../../../assets/icons';
import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import { MagicWandSvg } from '../../../../assets/icons/MagicWandSvg';
import theme from '../../../../assets/theme';
import useAccessBadge, { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import {
  allPromptOutputsSelector,
  attachmentsSelector,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useSingleSlidePromptTestCase from '../../../../hooks/useSingleSlidePromptTestCase';
import {
  getAvailablePromptDependencies,
  useVisiblePromptDependentInputValues,
  useVisiblePromptTestCaseInputs,
} from '../../../../hooks/useVisiblePromptTestCaseInputs';
import {
  ById,
  LocalPromptDependency,
  Prompt,
  PromptInput,
  PromptModels,
  promptModels,
  PromptSingleTestCaseResultType,
} from '../../../../services/backendService/types';
import { Input } from '../../../_atoms/_Input';
import Select from '../../../_atoms/_Select/Select';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { parseSlateStringToText } from '../../../SlideComponents/slateUtil';
import { Slide } from '../../../SlideComponents/slide.types';
import {
  AiFeedbackType,
  createAiFeedbackPrompt,
  getPromptTemplate,
} from '../PromptsTab/newPromptTemplates';
import { findDependencies } from '../PromptsTab/PromptBuilder';
import { PromptTextEditor } from '../PromptsTab/PromptTextEditor';

const AiFeedbackPromptBuilder = ({
  onSlideChange,
  slide,
  promptInputs,
  presentationId,
  isGenerator,
  isExpanded,
}: {
  onSlideChange: (slide: Slide) => void;
  slide: Slide;
  promptInputs?: PromptInput[];
  presentationId: string;
  isGenerator?: boolean;
  isExpanded?: boolean;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);

  const promptDependentInputs = useDesignerUIContext(
    context => context.state.promptDependentInputs,
  );

  const isPassagesEnabled = useFlag('nativepassages');

  const lessonAttachments = useDesignerDataContext(attachmentsSelector);

  const { t } = useTranslation('Host');
  const [studentAnswerExample, setStudentAnswerExample] = useState<string>('');
  const [isEditingPrompt, setIsEditingPrompt] = useState<boolean>(false);
  const metrics = useMetrics();
  const { generate, isLoading } = useSingleSlidePromptTestCase(presentationId, slide.id);

  const prompt = useMemo(() => {
    if (slide.prompts?.[0]) return slide.prompts?.[0];
    return createAiFeedbackPrompt();
  }, [slide]);

  const testCaseResult = useMemo(() => {
    if (promptDependentInputs) return promptDependentInputs[`${prompt.id}.${slide.id}`];
  }, [promptDependentInputs, slide.id, prompt.id]);

  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);

  const availablePromptDependencies = useMemo(() => {
    const studentAnswerField: LocalPromptDependency = {
      id: `slide-question-answer`,
      type: 'live',
      label: `Student response`,
      formType: 'shortText',
    };
    const questionField: LocalPromptDependency = {
      id: `slide-question`,
      type: 'live',
      label: `Slide question`,
      formType: 'shortText',
    };

    const lessonAttachmentDependencies = (isPassagesEnabled
      ? lessonAttachments || []
      : []
    ).map(attachment => {
      const dep: LocalPromptDependency = {
        id: attachment.attachmentId,
        type: 'live',
        label: parseSlateStringToText(attachment.title),
        formType: 'longText',
      };
      return dep;
    });
    if (!isGenerator)
      return [studentAnswerField, questionField, ...lessonAttachmentDependencies];

    const generalAvailablePromptDependencies = getAvailablePromptDependencies({
      promptId: prompt.id,
      promptOutputs,
      promptInputs,
    });

    return [
      ...generalAvailablePromptDependencies,
      studentAnswerField,
      questionField,
      ...lessonAttachmentDependencies,
    ];
  }, [
    isGenerator,
    promptInputs,
    promptOutputs,
    prompt.id,
    lessonAttachments,
    isPassagesEnabled,
  ]);

  const systemInstructionText = useMemo(() => {
    const systemMessage = prompt.messages.find(message => message.role === 'system');
    return systemMessage?.content;
  }, [prompt]);
  const question = useMemo(() => {
    const aiQuestionElement = slide.elements.find(
      element =>
        element.lockedFromDeletion &&
        (element.type === 'text' || element.type === 'generatorText'),
    );
    if (aiQuestionElement) {
      return parseSlateStringToText(aiQuestionElement.textValue);
    }
  }, [slide]);
  const dependencies = useMemo(() => findDependencies(prompt), [prompt]);

  const updatePromptDependentInputs = useCallback(
    (payload: { id: string; value: string; type: PromptSingleTestCaseResultType }) => {
      dispatch({
        type: DesignerUIStateActions.updatePromptDependentInputs,
        payload,
      });
    },
    [dispatch],
  );

  const { visibleInputs } = useVisiblePromptTestCaseInputs({
    availablePromptDependencies,
    promptDependencies: dependencies,
    promptInputs,
  });

  const { visiblePromptDependentInputValues } = useVisiblePromptDependentInputValues({
    visibleInputs,
    promptDependentInputs,
  });

  useEffect(() => {
    //check if there is cachedata and if there is find the input dependencies and set the default value
    if (promptDependentInputs) {
      //find the student answer prompt and set the value
      const studentAnswerPrompt =
        promptDependentInputs[`slide-question-answer.${slide.id}`];
      if (studentAnswerPrompt) {
        setStudentAnswerExample(studentAnswerPrompt.value);
      }

      return;
    }
  }, [promptDependentInputs, slide.id]);

  const promptContentSnippet = useMemo(() => {
    const userMessage = prompt.messages.find(message => message.role === 'user');
    if (!userMessage) return '';
    const content = parseSlateStringToText(userMessage.content);
    return `${content.slice(0, 59).trim()}....`;
  }, [prompt]);

  const handleGenerateSinglePromptTestCase = useCallback(() => {
    let testCaseInputs: ById<string> = {};

    // add default values form the dependencies themselves
    dependencies.forEach(dependency => {
      const foundInput = promptInputs?.find(
        input => input.id === dependency.dependencyId,
      );

      if (foundInput && foundInput.defaultValue) {
        testCaseInputs = {
          ...testCaseInputs,
          [dependency.dependencyId]: foundInput.defaultValue as string,
        };
      }
    });

    // Overwrite the default values with the prompt dependent inputs
    Object.values(visiblePromptDependentInputValues).forEach(dependency => {
      testCaseInputs = {
        ...testCaseInputs,
        [dependency.id]: dependency.value,
      };
    });

    const inputData = Object.keys(testCaseInputs).filter(key => key.startsWith('input'));
    const outputData = Object.keys(testCaseInputs).filter(key =>
      key.startsWith('prompt'),
    );
    const userInputFormData = inputData.map(key => ({
      id: key,
      type: 'string',
      value: testCaseInputs[key],
    }));
    userInputFormData.push({
      id: 'slide-question-answer',
      type: 'string',
      value: studentAnswerExample,
    });
    userInputFormData.push({
      id: 'slide-question',
      type: 'string',
      value: parseSlateStringToText(question),
    });

    generate({
      presentationId,
      slideId: slide.id,
      userInputFormData,
      resourceMap: outputData.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            id: key,
            type: 'string',
            value: testCaseInputs[key],
          },
        }),
        {},
      ),
    });
  }, [
    dependencies,
    generate,
    presentationId,
    promptInputs,
    question,
    slide.id,
    studentAnswerExample,
    visiblePromptDependentInputValues,
  ]);

  const promptTemplate = useMemo(() => {
    const promptTemplate = getPromptTemplate(prompt);
    return promptTemplate;
  }, [prompt]);

  const runTestcaseDisabled = useMemo(() => {
    //check if there is any input that has not been filled
    const hasEmptyInput = visibleInputs.some(dependency => {
      return !visiblePromptDependentInputValues?.[dependency.id];
    });
    return !studentAnswerExample || hasEmptyInput;
  }, [visiblePromptDependentInputValues, visibleInputs, studentAnswerExample]);

  const customizeTooltip = useMemo(() => {
    if (
      !userAccessBadge ||
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
    ) {
      return 'Only available on the School plan.';
    }
    if (slide.generatorId && slide.generatorId !== 'dynamicLesson') {
      return `This lesson's AI feedback is pre-made by our Curriculum team and is not editable to ensure the best student experience.`;
    }

    return `As a Curipod ${
      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.enterprise
        ? 'school plan'
        : 'premium'
    } subscriber, you can customize this AI feedback.`;
  }, [slide.generatorId, userAccessBadge]);

  return (
    <>
      <StyledFormInputDiv>
        <p
          style={{
            fontSize: 14,
          }}
        >
          {t('Answer character limit')}
          <Tooltip
            onVisibleChange={visible =>
              visible && metrics.logEvent('Creator.Tooltip.answerCharacterLimit.Show')
            }
            title={t(
              'Students will be able to write up to this number of characters (excluding spaces)',
            )}
          >
            <TooltipIconWrapper>
              <QuestionMarkSvg />
            </TooltipIconWrapper>
          </Tooltip>
        </p>
        <Radio.Group
          value={slide?.responseCharacterLimit || 1000}
          buttonStyle="solid"
          style={{ width: '100%' }}
        >
          <StyledRadioButton
            value={475}
            onClick={e => {
              const radioElement = e.currentTarget as HTMLInputElement;
              metrics.logEvent('Creator.OnChange.ResponseCharacterLimit', {
                characterLimit: 475,
              });
              onSlideChange({
                ...slide,
                responseCharacterLimit: Number(radioElement.value),
              });
            }}
          >
            475
          </StyledRadioButton>
          <StyledRadioButton
            value={1000}
            onClick={e => {
              const radioElement = e.currentTarget as HTMLInputElement;
              metrics.logEvent('Creator.OnChange.ResponseCharacterLimit', {
                characterLimit: 1000,
              });
              onSlideChange({
                ...slide,
                responseCharacterLimit: Number(radioElement.value),
              });
            }}
          >
            1000
          </StyledRadioButton>
          <StyledRadioButton
            value={2300}
            onClick={e => {
              if (
                isReady &&
                (!userAccessBadge ||
                  userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium)
              ) {
                metrics.logEvent('Creator.OnChange.ResponseCharacterLimit.PaywallPrompt');
                promptPaywall({
                  label: 'Increase character limit',
                  description: 'AI Feedback',
                });
              } else {
                const radioElement = e.currentTarget as HTMLInputElement;
                metrics.logEvent('Creator.OnChange.ResponseCharacterLimit', {
                  characterLimit: 2300,
                });
                onSlideChange({
                  ...slide,
                  responseCharacterLimit: Number(radioElement.value),
                });
              }
            }}
          >
            <IconWrapper>
              2300{' '}
              <EnterprisePlanIcon
                color={
                  !userAccessBadge ||
                  userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
                    ? theme.colors.yellow
                    : 'currentColor'
                }
              />
            </IconWrapper>
          </StyledRadioButton>
          <StyledRadioButton
            value={5000}
            onClick={e => {
              if (
                isReady &&
                (!userAccessBadge ||
                  userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium)
              ) {
                metrics.logEvent('Creator.OnChange.ResponseCharacterLimit.PaywallPrompt');
                promptPaywall({
                  label: 'Increase character limit',
                  description: 'AI Feedback',
                });
              } else {
                const radioElement = e.currentTarget as HTMLInputElement;
                metrics.logEvent('Creator.OnChange.ResponseCharacterLimit', {
                  characterLimit: 5000,
                });
                onSlideChange({
                  ...slide,
                  responseCharacterLimit: Number(radioElement.value),
                });
              }
            }}
          >
            <IconWrapper>
              5000{' '}
              <EnterprisePlanIcon
                color={
                  !userAccessBadge ||
                  userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
                    ? theme.colors.yellow
                    : 'currentColor'
                }
              />
            </IconWrapper>
          </StyledRadioButton>
        </Radio.Group>
        <Divider style={{ margin: '20px 0 10px 0' }} />
        <p style={{ margin: '5px 0 0 0', fontSize: 14 }}>Prompt template</p>
        <Select
          value={promptTemplate}
          disabled={
            slide.generatorId !== undefined &&
            (!isGenerator || slide.generatorId !== 'dynamicLesson')
              ? true
              : false
          }
          iconValue={
            promptTemplate === 'custom' ? (
              <IconWrapper>
                <EnterprisePlanIcon
                  color={
                    !userAccessBadge ||
                    userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
                      ? theme.colors.yellow
                      : theme.colors.black
                  }
                />
              </IconWrapper>
            ) : (
              undefined
            )
          }
          onSelect={val => {
            metrics.logEvent('Creator.AiFeedback.ChangePromptTemplate', {
              promptTemplate: val,
            });
            if (
              val === 'custom' &&
              isReady &&
              (!userAccessBadge ||
                userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium)
            ) {
              metrics.logEvent('Creator.AiFeedback.ChangePromptTemplate.PaywallPrompt');
              promptPaywall({
                label: 'Change prompt template',
                description: 'AI Feedback',
              });
              return;
            }

            const newPrompt = createAiFeedbackPrompt(val as AiFeedbackType);
            if (val === 'custom') {
              newPrompt.promptOutput = prompt.promptOutput;
              newPrompt.messages = prompt.messages.map(message => ({
                ...message,
                content: message.content + '\n',
              }));
            }
            onSlideChange({
              ...slide,
              prompts: [newPrompt],
            });
          }}
          options={[
            {
              displayName: 'ELA short answer',
              value: 'elaShortAnswer',
            },
            {
              displayName: 'Science short answer',
              value: 'scienceShortAnswer',
            },
            {
              displayName: 'Social studies short answer',
              value: 'socialStudiesShortAnswer',
            },
            {
              displayName: 'Custom',
              value: 'custom',
              icon: (
                <IconWrapper>
                  <EnterprisePlanIcon
                    color={
                      !userAccessBadge ||
                      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
                        ? theme.colors.yellow
                        : theme.colors.black
                    }
                  />
                </IconWrapper>
              ),
            },
          ]}
        />
        <Divider style={{ margin: '20px 0 10px 0' }} />
        {!isEditingPrompt && !isExpanded && (
          <>
            <DisabledContainer
              onClick={() => {
                metrics.logEvent('Creator.AiFeedback.EditPrompt');
                if (
                  !userAccessBadge ||
                  userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium ||
                  (slide.generatorId && slide.generatorId !== 'dynamicLesson')
                ) {
                  metrics.logEvent('Creator.AiFeedback.EditPrompt.PaywallPrompt');
                  promptPaywall({
                    label: 'Customize prompt',
                    description: 'AI Feedback',
                  });
                } else {
                  setIsEditingPrompt(true);
                }
              }}
            >
              {promptContentSnippet}
            </DisabledContainer>
            <span
              style={{ display: 'flex', cursor: 'pointer' }}
              onClick={() => {
                metrics.logEvent('Creator.AiFeedback.EditPrompt');
                if (
                  !userAccessBadge ||
                  userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
                ) {
                  metrics.logEvent('Creator.AiFeedback.EditPrompt.PaywallPrompt');
                  promptPaywall({
                    label: 'Customize prompt',
                    description: 'AI Feedback',
                  });
                  return;
                }
                if (slide.generatorId && slide.generatorId !== 'dynamicLesson') {
                  return;
                }
                setIsEditingPrompt(true);
              }}
            >
              {''}
              <Tooltip title={customizeTooltip}>
                <span style={{ display: 'flex' }}>
                  <IconWrapper
                    color={
                      !userAccessBadge ||
                      userAccessBadge.license.coarseLevel === LicenseCoarseLevels.freemium
                        ? theme.colors.yellow
                        : 'currentColor'
                    }
                  >
                    <EnterprisePlanIcon />
                  </IconWrapper>
                  <span style={{ textDecoration: 'underline', marginLeft: '5px' }}>
                    Customize
                  </span>
                </span>
              </Tooltip>
            </span>
          </>
        )}
        {(isEditingPrompt || isExpanded) && (
          <EditSlidePrompt
            onSlideChange={onSlideChange}
            slide={slide}
            prompt={prompt}
            systemInstructionText={systemInstructionText}
            availablePromptDependencies={availablePromptDependencies}
            promptTemplate={promptTemplate}
          />
        )}
        <Divider style={{ margin: '20px 0 10px 0' }} />
        {!isExpanded && (
          <div>
            <p style={{ margin: '10px 0', flex: 1, fontSize: 16 }}>Test the feedback</p>
            <p style={{ marginBottom: 5, fontSize: 14 }}>Question</p>

            {question && (
              <Tooltip
                title={
                  'This question field is the same as the question field in the slide'
                }
                mouseEnterDelay={1}
              >
                <ActivityQuestion>{question}</ActivityQuestion>
              </Tooltip>
            )}

            {visibleInputs.map(dependency => {
              const dependencyId = dependency?.id;
              if (!dependencyId) return null;

              if (dependency.formType === 'dropdown') {
                return (
                  <Select
                    label={dependency?.label}
                    placeholder={dependency?.defaultValue || 'Select value'}
                    onSelect={val => {
                      updatePromptDependentInputs({
                        id: dependencyId,
                        value: val as string,
                        type:
                          promptDependentInputs?.[dependencyId]?.type ||
                          dependency.formType,
                      });
                    }}
                    value={promptDependentInputs?.[dependencyId]?.value}
                    options={dependency?.constraints?.options || []}
                  ></Select>
                );
              }

              if (dependency.formType === 'longText') {
                return (
                  <div key={dependencyId}>
                    <TextArea
                      label={dependency?.label}
                      value={promptDependentInputs?.[dependencyId]?.value}
                      placeholder={dependency?.defaultValue || 'Add a value'}
                      style={{ width: '100%' }}
                      maxLength={dependency.constraints?.maxLength}
                      symbolCount={true}
                      onChange={e =>
                        updatePromptDependentInputs({
                          id: dependencyId,
                          value: e.target.value as string,
                          type:
                            promptDependentInputs?.[dependencyId]?.type ||
                            dependency.formType,
                        })
                      }
                    />
                  </div>
                );
              }

              return (
                <div key={dependencyId}>
                  <Input
                    label={dependency?.label}
                    value={promptDependentInputs?.[dependencyId]?.value}
                    placeholder={dependency?.defaultValue || 'Add a value'}
                    onChange={e =>
                      updatePromptDependentInputs({
                        id: dependencyId,
                        value: e.target.value as string,
                        type:
                          promptDependentInputs?.[dependencyId]?.type ||
                          dependency.formType,
                      })
                    }
                  />
                </div>
              );
            })}
            <p style={{ margin: '10px 0', flex: 1, fontSize: 14 }}>Student answer</p>
            <TextArea
              autoSize={{ minRows: 2, maxRows: 20 }}
              style={{ width: '100%' }}
              value={studentAnswerExample}
              maxLength={slide.responseCharacterLimit || undefined}
              symbolCount={true}
              onChange={e => setStudentAnswerExample(e.target.value)}
            />
            <Button
              styles={{ margin: '10px 0' }}
              icon={<MagicWandSvg />}
              onClick={() => handleGenerateSinglePromptTestCase()}
              loading={isLoading}
              disabled={runTestcaseDisabled}
            >
              Test the feedback
            </Button>

            {testCaseResult && (
              <ResultsContainer>
                <span>Result</span>
                <p style={{ whiteSpace: 'pre-wrap' }}>{testCaseResult.value}</p>
              </ResultsContainer>
            )}
          </div>
        )}
        {paywallComponent}
      </StyledFormInputDiv>
    </>
  );
};

const EditSlidePrompt = ({
  onSlideChange,
  prompt,
  slide,
  availablePromptDependencies,
  systemInstructionText,
  promptTemplate,
}: {
  onSlideChange: (slide: Slide) => void;
  prompt: Prompt;
  slide: Slide;
  systemInstructionText?: string;
  promptTemplate?: string;
  availablePromptDependencies: LocalPromptDependency[];
}) => {
  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');
  const metrics = useMetrics();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { isReady, promptPaywall } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const handleSystemInstructionChange = (val: string) => {
    const systemMessage = prompt.messages.find(message => message.role === 'system');
    if (systemMessage) {
      onSlideChange({
        ...slide,
        prompts: [
          {
            ...prompt,
            messages: prompt.messages.map(message =>
              message.role === 'system' ? { ...systemMessage, content: val } : message,
            ),
          },
        ],
      });
    }
  };
  const handlePromptMessageChanged = (val: string, index: number) => {
    const newMessage = { ...prompt.messages[index] };
    if (newMessage) {
      onSlideChange({
        ...slide,
        prompts: [
          {
            ...prompt,
            messages: prompt.messages.map((message, i) =>
              i === index ? { ...newMessage, content: val } : message,
            ),
          },
        ],
      });
    }
  };
  return (
    <>
      <Collapse defaultActiveKey={[]} ghost style={{ marginTop: '10px' }}>
        <Collapse.Panel
          header="Advanced options"
          key="1"
          style={{ paddingLeft: 0, fontWeight: 'bold' }}
        >
          {isAITemplatesEnabled && (
            <>
              <p style={{ paddingTop: '20px', fontSize: '14px' }}>Prompt model</p>
              <Select
                value={prompt.promptOutput.resource.model}
                onSelect={val => {
                  metrics.logEvent('Creator.AiFeedback.ChangePromptTemplate', {
                    promptTemplate: val,
                  });
                  if (val === 'custom') {
                    if (
                      isReady &&
                      (!userAccessBadge ||
                        userAccessBadge.license.coarseLevel ===
                          LicenseCoarseLevels.freemium)
                    ) {
                      metrics.logEvent(
                        'Creator.AiFeedback.ChangePromptTemplate.PaywallPrompt',
                      );
                      promptPaywall();
                      return;
                    }
                  }

                  onSlideChange({
                    ...slide,
                    prompts: [
                      {
                        ...prompt,
                        promptOutput: {
                          ...prompt.promptOutput,
                          resource: {
                            ...prompt.promptOutput.resource,
                            model: val as PromptModels,
                          },
                        },
                      },
                    ],
                  });
                }}
                options={[
                  {
                    displayName: 'GPT-4o',
                    value: promptModels.gpt4o,
                  },
                  {
                    displayName: 'GPT-4o-mini',
                    value: promptModels.gpt4omini,
                  },
                  {
                    displayName: 'GPT-4',
                    value: promptModels.gpt4,
                  },
                  {
                    displayName: 'GPT-4-turbo',
                    value: promptModels.gpt4Turbo,
                  },
                ]}
              />
            </>
          )}
          <p style={{ paddingTop: '20px', fontSize: '14px', display: 'flex' }}>
            Creativity: {prompt.temperature || 1}
            <Tooltip
              mouseEnterDelay={0.5}
              title={
                'High creativity means that the AI feedback will be more creative and less likely to be similar to other ai feedbacks, but also more unpredictable and more likely to be incorrect. A low temperature means the feedback will be more consistent, but at the expense of creativity, and it might feel stale'
              }
            >
              <IconWrapper width="15px" height="15px" style={{ marginLeft: '10px' }}>
                <QuestionMarkSvg />
              </IconWrapper>
            </Tooltip>
          </p>
          <Slider
            min={0}
            max={2}
            step={0.01}
            value={prompt.temperature || 0.7}
            onChange={e => {
              metrics.logEvent('Creator.AiFeedback.ChangePromptTemperature', {
                temperature: e,
              });

              onSlideChange({
                ...slide,
                prompts: [
                  {
                    ...prompt,
                    temperature: e,
                  },
                ],
              });
            }}
          />
          <Divider />
        </Collapse.Panel>
      </Collapse>

      <div style={{ display: 'flex', marginTop: '20px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>
          System context instruction
        </p>
      </div>
      <PromptContainer>
        <PromptTextEditor
          availablePromptDependencies={availablePromptDependencies}
          value={systemInstructionText}
          slideId={slide.id}
          promptTemplate={promptTemplate}
          maxLength={6000}
          placeholder={'You are a helpful assistant...'}
          onChange={handleSystemInstructionChange}
        />
      </PromptContainer>
      <div style={{ display: 'flex', marginTop: '20px' }}>
        <p style={{ marginBottom: 5, flex: 1, fontSize: 14 }}>Prompt instruction</p>
      </div>
      <MessageContainer>
        {prompt.messages.map((message, i) =>
          i === 0 ? null : (
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flex: 1 }}>
                <PromptContainer>
                  <PromptTextEditor
                    key={`${i}`}
                    value={message.content}
                    slideId={slide.id}
                    promptTemplate={promptTemplate}
                    placeholder={'Create a question about...'}
                    availablePromptDependencies={availablePromptDependencies}
                    onChange={val => handlePromptMessageChanged(val, i)}
                    maxLength={6000}
                  />
                </PromptContainer>
              </div>
            </div>
          ),
        )}
      </MessageContainer>
    </>
  );
};
const MessageContainer = styled.div`
  & > *:not(:first-child) {
    margin-top: 5px;
  }
  & > *:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const StyledFormInputDiv = styled.div`
  margin-bottom: 20px;
  max-width: 100%;
  p {
    font-weight: bold;
    color: #282f33;
    margin-bottom: 5px;
  }
  .ant-collapse-header,
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const StyledRadioButton = styled(Radio.Button)`
  width: 25%;
  span {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  svg {
    padding-left: 10px;
    box-sizing: content-box;
  }
`;

const TooltipIconWrapper = styled.span`
  margin-left: 10px;
  &:hover {
    cursor: pointer;
  }
  svg {
    height: 16px;
    width: 16px;
    color: ${theme.colors.black};
  }
`;
const ActivityQuestion = styled.div`
  font-weight: bold;
  font-size: 12px;
  background-color: #e9ecef; /* Light gray background */
  color: #6c757d; /* Dark gray text */

  border: 1px solid #ced4da; /* Gray border */
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  margin-top: 10px;

  /* Optional: Add a "not-allowed" cursor to indicate that the field is disabled */
  cursor: not-allowed;
`;

const ResultsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  gap: 10px;
`;

const PromptContainer = styled.div`
  p {
    font-size: 14px;
    font-weight: bold;
    color: #282f33;
  }
`;

const DisabledContainer = styled.div`
  font-weight: bold;
  font-size: 12px;
  background-color: #e9ecef; /* Light gray background */
  color: #6c757d; /* Dark gray text */

  border: 1px solid #ced4da; /* Gray border */
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  margin-top: 10px;
  cursor: pointer;
`;
export default AiFeedbackPromptBuilder;
